import React, { useRef, useState, useEffect } from "react";
import './Timeline.css'
import { Mic, Piano } from "lucide-react";




const CONTROL_WIDTH = 160;
const BEAT_WIDTH = 80;
const TRACK_HEIGHT = 80;
const GRID_HEIGHT = 24;

const NOTE_ORDER = [
  "C6", "B5", "A#5", "A5", "G#5", "G5", "F#5", "F5", "E5", "D#5", "D5", "C#5", "C5",
  "B4", "A#4", "A4", "G#4", "G4", "F#4", "F4", "E4", "D#4", "D4", "C#4", "C4",
  "B3", "A#3", "A3", "G#3", "G3", "F#3", "F3", "E3", "D#3", "D3", "C#3", "C3"
];

const getNoteRowIndex = (note) => NOTE_ORDER.indexOf(note);
const NOTE_HEIGHT = 2;



export default function Timeline({
  tracks,
  numBeats,
  selectedTrackId,
  onSelectTrack,
  playheadPosition,
  onMoveClip,
  volumeRef,
  muteRef,
  onDeleteClip,
  onSetClipVolume,
  onScrubPlayhead,
  onVolumeChange,
  onToggleMute,
  stepIndex,
  setHasInteracted,
  lesson
}) {
  const timelineRef = useRef(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingPlayhead || !timelineRef.current) return;
      const bounds = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - bounds.left - CONTROL_WIDTH;
      const percent = x / (BEAT_WIDTH * numBeats);
      const newPosition = Math.max(0, Math.min(numBeats, percent * numBeats));
      onScrubPlayhead(newPosition);
    };

    const handleMouseUp = () => setIsDraggingPlayhead(false);

    if (isDraggingPlayhead) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingPlayhead, numBeats, onScrubPlayhead]);

  return (
    <div
      ref={timelineRef}
      style={{
        overflowX: "auto",
        overflowY: "visible",
        whiteSpace: "nowrap",
        position: "relative",
        width: "100%",
        backgroundColor: "#1e1e1e",
        padding: "8px 0",
        userSelect: "none",
      }}
    >
      {/* Grid */}
      <div
        style={{
          width: `${CONTROL_WIDTH}px`,
          height: GRID_HEIGHT,
          display: "inline-block",
        }}
      />

      {/* Actual grid */}
      <div
        style={{
          width: `${numBeats * BEAT_WIDTH}px`,
          display: "flex",
          height: GRID_HEIGHT,
          borderBottom: "1px solid #555",
          marginLeft: CONTROL_WIDTH,
        }}
        onClick={(e) => {
          const bounds = e.currentTarget.getBoundingClientRect();
          const clickX = e.clientX - bounds.left;
          const percent = clickX / bounds.width;
          const newPosition = percent * numBeats;
          onScrubPlayhead(newPosition);
        }}
      >
        {[...Array(numBeats)].map((_, i) => (
          <div
            key={i}
            style={{
              width: `${BEAT_WIDTH}px`,
              borderRight: "1px solid #333",
              textAlign: "center",
              fontSize: "10px",
              color: "#aaa",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Playhead */}
      <div
        onMouseDown={() => setIsDraggingPlayhead(true)}
        style={{
          position: "absolute",
          top: 0,
          left: `${(playheadPosition / numBeats) * (BEAT_WIDTH * numBeats) +
            CONTROL_WIDTH
            }px`,

          width: "12px",
          marginLeft: "4px",
          height: `${tracks.length * TRACK_HEIGHT + 3 * GRID_HEIGHT}px`,
          zIndex: 10,
          cursor: "ew-resize",
          pointerEvents: "auto",
        }}
      >
        <div
          style={{
            width: "2px",
            height: "100%",
            backgroundColor: "red",
            margin: "0 auto",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Track Lanes */}
      {tracks.map((track) => (
        <div
          key={track.id}
          style={{
            display: "flex",
            height: "80px",
            marginBottom: "4px",
            backgroundColor:
              track.id === selectedTrackId ? "#1a2a3a" : "#2a2a2a",
            border:
              track.id === selectedTrackId
                ? "2px solid #3fa9f5"
                : "1px solid #444",
          }}
        >
          {/* Left Controls Panel */}
          <div
            style={{
              width: `${CONTROL_WIDTH}px`,
              padding: "4px",
              display: "flex",
              flexDirection: "row", // ⬅️ now row instead of column
              alignItems: "center",
              color: "#ccc",
              borderRight: "1px solid #555",
              height: "100%", // make sure it fills the track height
            }}
          >
            {/* Icon Column */}
            <div
              style={{
                width: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              {track.instrument === "microphone" ? (
                <Mic size={24} style={{ color: "#d66" }} />
              ) : (
                <Piano size={24} style={{ color: "#6ad" }} />
              )}
            </div>

            {/* Controls Column */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              <input
                type="range"
                className={
                  lesson?.[stepIndex]?.target === "volume" ? "highlight-button" : ""
                }
                ref={volumeRef}
                min={0}
                max={1}
                step={0.01}
                value={track.volume}
                onChange={(e) => {
                  onVolumeChange(track.id, parseFloat(e.target.value));
                  if (lesson?.[stepIndex]?.target === "volume") {
                    setHasInteracted(true);
                  }
                }}
              />
              <button
                className={`mute-button ${lesson?.[stepIndex]?.target === "mute" ? "highlight-button" : ""
                  }`}
                ref={muteRef}
                onClick={() => {
                  onToggleMute(track.id);
                  if (lesson?.[stepIndex]?.target === "mute") {
                    setHasInteracted(true);
                  }
                }}
                style={{ fontSize: "10px" }}
              >
                {track.muted ? "Unmute" : "Mute"}
              </button>
            </div>
          </div>


          {/* Right Clip Area */}
          <div
            style={{
              position: "relative",
              flex: 1,
              height: TRACK_HEIGHT,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSelectTrack(track.id);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const draggedTrackId = parseInt(
                e.dataTransfer.getData("trackId")
              );
              const draggedClipIndex = parseInt(
                e.dataTransfer.getData("clipIndex")
              );
              const bounds = e.currentTarget.getBoundingClientRect();
              const dropX = e.clientX - bounds.left;
              const percent = dropX / bounds.width;
              const newStart = percent * numBeats;
              onMoveClip(draggedTrackId, draggedClipIndex, newStart);
            }}
          >
            {track.clips
              .filter((clip) => clip.url || clip.isVirtual || clip.isRecordingClip)
              .map((clip, clipIndex) => {
                const clipStartPercent = (clip.start / numBeats) * 100;
                const clipWidthPercent = (clip.duration / numBeats) * 100;

                return (
                  <div
                    key={clipIndex}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("trackId", track.id);
                      e.dataTransfer.setData("clipIndex", clipIndex);
                    }}
                    style={{
                      position: "absolute",
                      top: "2px",
                      left: `${clipStartPercent}%`,
                      width: `${clipWidthPercent}%`,
                      height: TRACK_HEIGHT,
                      backgroundColor: clip.isRecordingClip
                        ? "red"
                        : track.muted
                          ? "#888"
                          : "#3fa9f5",
                      opacity: track.muted ? 0.5 : 1,
                      cursor: "grab",
                      borderRadius: "4px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    }}
                    title={
                      clip.isRecordingClip
                        ? "Recording..."
                        : `Start: ${clip.start.toFixed(2)}s`
                    }
                  >
                    {clip.notes?.map((note, idx) => {
                      const noteIndex = getNoteRowIndex(note.note);
                      if (noteIndex === -1) return null;

                      const top = noteIndex * NOTE_HEIGHT;
                      const left = ((note.start - clip.start) / clip.duration) * 100;
                      const width = (note.duration / clip.duration) * 100;

                      return (
                        <div
                          key={idx}
                          style={{
                            position: "absolute",
                            top: `${top}px`,
                            left: `${left}%`,
                            width: `${width}%`,
                            height: `${NOTE_HEIGHT}px`,
                            backgroundColor: "#a6d4fa",
                          }}
                        />
                      );
                    })}

                  </div>
                );
              })}

          </div>
        </div>
      ))}
    </div>
  );
}
